import * as React from "react";
import { Ban, MapPin, Pencil, Plus, RotateCcw } from "lucide-react";
import type { Pagination as PageMeta, Site } from "@taskiq/shared";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useConfirm, useDiscardGuard } from "@/components/ui/confirm-context";
import { EmptyState, ErrorState, FormMessage } from "@/components/ui/Feedback";
import { Field, Form, Input } from "@/components/ui/Field";
import { IconButton } from "@/components/ui/IconButton";
import { Identity } from "@/components/ui/Identity";
import { IconTile } from "@/components/ui/IconTile";
import { Modal } from "@/components/ui/Modal";
import { FilterBar } from "@/components/ui/FilterBar";
import { FilterSelect } from "@/components/ui/FilterSelect";
import { Pagination } from "@/components/ui/Pagination";
import { PageHeader } from "@/components/ui/PageHeader";
import { SearchInput } from "@/components/ui/SearchInput";
import { useToast } from "@/components/ui/toast-context";
import { SkeletonRows, Table, Td, TdSpan, Th, Tr } from "@/components/ui/Table";
import { TimezoneSelect, browserTimezone } from "@/components/ui/TimezoneSelect";
import { api, apiErrorMessage } from "@/lib/api";
import { PLACEHOLDER } from "@/lib/placeholders";
import { useAuth } from "@/lib/auth-context";
import { useTenancy } from "@/lib/tenancy";
import { LIST_PAGE_SIZE } from "@/lib/paging";
import { SEARCH_DEBOUNCE_MS, useDebounced } from "@/lib/useDebounced";
import { useApi } from "@/lib/useApi";

type SiteRow = Site & { company: { id: string; name: string; slug: string } | null };

interface SiteListResponse {
  sites: SiteRow[];
  pagination: PageMeta;
}

/**
 * Sites list, for the company the console is currently working in.
 *
 * The clearest demonstration in the console that reach is real: an admin sees
 * their company's sites, a field officer sees the ones they are assigned. No
 * role branching happens on this page - the API returns what the caller may see
 * and RLS guarantees it, so this component just renders rows.
 *
 * ── It asks for one company, and that is not a filter ───────────────────────
 *
 * It used to ask for `/sites` flat, which for a superadmin returns every site
 * belonging to every customer on the platform in one alphabetical list. That is
 * not a useful screen - the topbar says "Visionify · Hyderabad" while the table
 * shows another tenant's depots, and the Company column existed purely to tell
 * them apart. Naming `company_id` makes the page agree with the switcher above
 * it, and the column stops being needed at all.
 *
 * Reach still does the real work underneath: this narrows a set that was already
 * legal, exactly like the site id in a `/sites/12/users` path. A caller who
 * names a company they cannot reach gets an empty list from RLS, not a leak.
 *
 * Creating a site is here rather than buried in settings because it is the first
 * thing a new workspace needs and the hard prerequisite for the second: an
 * invitation must name a default site, so a console with no way to add one has
 * an invitation flow that dead-ends on its own form.
 */
export function Sites() {
  const { can } = useAuth();
  const tenancy = useTenancy();

  const [search, setSearch] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [page, setPage] = React.useState(1);
  const trimmed = useDebounced(search, SEARCH_DEBOUNCE_MS).trim();

  const query = new URLSearchParams({ page: String(page), pageSize: String(LIST_PAGE_SIZE) });
  if (tenancy.activeCompanyId !== null) {
    query.set("company_id", String(tenancy.activeCompanyId));
  }
  // Both filters are the API's, not this component's. `search` and `is_active`
  // have been in `listSitesQuery` since it was written and no control ever sent
  // them, so a company with more than a screenful of depots had no way to find
  // one and no way to see which were deactivated.
  if (trimmed) query.set("search", trimmed);
  if (status) query.set("is_active", status);

  const { data, error, isLoading, mutate } = useApi<SiteListResponse>(`/sites?${query}`);
  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<SiteRow | null>(null);

  const activeFilters = [trimmed, status].filter(Boolean).length;

  // Any narrowing moves the last page underneath you, and asking for page 3 of
  // one page of matches renders an empty table that reads as "nothing matches".
  React.useEffect(() => {
    setPage(1);
  }, [trimmed, status, tenancy.activeCompanyId]);

  const canCreate = can("site.write");

  const confirm = useConfirm();
  const toast = useToast();
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [rowError, setRowError] = React.useState<Record<string, string>>({});

  /**
   * Deactivating and reactivating a yard.
   *
   * This used to be absent on the argument, written into `SiteDialog` below,
   * that Postgres refuses to close a site anyone still defaults to - so the
   * control "would be a switch that fails with a number in it" and needed a user
   * list to explain itself first.
   *
   * The objection was right and the mechanism it wanted already existed.
   * `app.enforce_site_write_rules` raises a `check_violation` **naming the
   * count**, `fromPostgrest` maps 23514 to a 400 carrying the trigger's message
   * verbatim, and `.row-error` puts it on the row that failed. What comes back
   * is "3 user(s) still have this site as their default; move them before
   * closing it", which is the sentence the missing list was going to produce.
   *
   * Deactivating is confirmed because its consequences reach past this page -
   * see the copy below. Reactivating is not: it is the reversal, and it takes
   * nothing away. Same asymmetry as Team and Companies.
   *
   * ── It used to say "close" and "reopen" ─────────────────────────────────
   *
   * A site was closed, a company was suspended and a person was deactivated -
   * three verbs for one column, `is_active`, on three screens an administrator
   * moves between in a single sitting. The words are all "deactivate" now. The
   * old ones were more evocative and that was the problem: "closed" reads as a
   * property of the yard (it shut down) rather than of the record (we switched
   * it off here), which is exactly the wrong thing to imply about a row whose
   * history stays readable.
   */
  async function toggleActive(site: SiteRow) {
    setRowError((prev) => {
      const next = { ...prev };
      delete next[site.id];
      return next;
    });

    if (site.is_active) {
      const ok = await confirm({
        title: `Deactivate ${site.name}?`,
        message:
          "Nobody will be able to be scheduled there, and technicians assigned to it lose access " +
          "until it is reactivated. Its appointments, visits and assessments stay on record, and " +
          "an administrator can still see them.",
        confirmLabel: "Deactivate",
        cancelLabel: "Leave it active",
        danger: true,
      });
      if (!ok) return;
    }

    setBusyId(site.id);
    try {
      await api(`/sites/${site.id}`, { method: "PATCH", body: { is_active: !site.is_active } });
      toast.success(`${site.name} ${site.is_active ? "deactivated" : "reactivated"}`);
      await mutate();
    } catch (err) {
      // Inline, not a toast. This one is almost always the "move these people
      // first" message, which is something to act on rather than something to
      // acknowledge - and a four-second toast is the wrong place for it.
      setRowError((prev) => ({
        ...prev,
        [site.id]: apiErrorMessage(err, "Could not change that location"),
      }));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Locations"
        description={tenancy.reachSentence}
        actions={
          canCreate && (
            <Button icon={<Plus />} onClick={() => setCreating(true)}>
              Add site
            </Button>
          )
        }
      />

      <FilterBar
        activeCount={activeFilters}
        onClear={() => {
          setSearch("");
          setStatus("");
        }}
        search={
          <SearchInput
            value={search}
            onChange={setSearch}
            label="Search locations by name"
            placeholder="Search locations"
          />
        }
      >
        <FilterSelect
          value={status}
          onChange={setStatus}
          label="Filter by status"
          icon={<MapPin />}
          active={Boolean(status)}
          options={[
            { value: "", label: "Any status" },
            { value: "true", label: "Active" },
            { value: "false", label: "Deactivated" },
          ]}
        />
      </FilterBar>

      {error && <ErrorState error={error} onRetry={() => void mutate()} />}

      {/* Suppressed while a failure is on screen with nothing cached behind it.
          An empty table under an error banner is a header strip with no body -
          it reads as a confident "there is nothing here", which is a different
          and much more alarming claim than "we could not load this". */}
      {!(error && !data) && (
        <>
          <Table
            head={
              <>
                <Th>Site</Th>
                <Th hideBelow="sm">Timezone</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </>
            }
          >
            {isLoading && (
              <SkeletonRows
                columns={[
                  { kind: "identity" },
                  { kind: "text", hideBelow: "sm" },
                  { kind: "badge", width: "narrow" },
                  { kind: "blank" },
                ]}
              />
            )}

            {data && data.sites.length === 0 && (
              <tr>
                <TdSpan colSpan={4}>
                  <EmptyState
                    icon={<MapPin />}
                    title={activeFilters > 0 ? "No locations match that" : "No locations yet"}
                    description={
                      activeFilters > 0
                        ? "Try a different name, or clear the filters."
                        : "Locations group technicians by crew, depot or client. Every assessment is filed against one."
                    }
                    action={
                      activeFilters === 0 &&
                      canCreate && <Button onClick={() => setCreating(true)}>Add your first</Button>
                    }
                  />
                </TdSpan>
              </tr>
            )}

            {data?.sites.map((site) => (
              <Tr key={site.id}>
                <Td>
                  {/* No company here, in the cell or as a subtitle. Every row on
                    this page belongs to the company named in the topbar, so
                    printing it once per row is the same word repeated down the
                    table. */}
                  <Identity
                    media={
                      <IconTile>
                        <MapPin />
                      </IconTile>
                    }
                    title={site.name}
                    subtitle={site.timezone}
                    subtitleOnlyBelow="sm"
                  />
                </Td>
                <Td hideBelow="sm" className="muted">
                  {site.timezone}
                </Td>
                <Td>
                  <div className="row-badges">
                    {/* Both states, always. Rendering a badge only for the
                    deactivated ones left every live site with an empty Status
                    cell, which reads as missing data rather than as "fine" -
                    and it is the column somebody scans precisely to confirm
                    nothing is switched off. */}
                    {site.is_active ? (
                      <Badge tone="ok">Active</Badge>
                    ) : (
                      <Badge tone="stop">Deactivated</Badge>
                    )}
                  </div>
                  {rowError[site.id] && (
                    <p role="alert" className="row-error">
                      {rowError[site.id]}
                    </p>
                  )}
                </Td>
                <Td align="right">
                  <div className="row-actions">
                    {canCreate && (
                      <IconButton
                        size="sm"
                        label={`Edit ${site.name}`}
                        onClick={() => setEditing(site)}
                      >
                        <Pencil />
                      </IconButton>
                    )}
                    {canCreate && (
                      <IconButton
                        size="sm"
                        tone={site.is_active ? "danger" : "default"}
                        label={
                          site.is_active ? `Deactivate ${site.name}` : `Reactivate ${site.name}`
                        }
                        disabled={busyId === site.id}
                        onClick={() => void toggleActive(site)}
                      >
                        {site.is_active ? <Ban /> : <RotateCcw />}
                      </IconButton>
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
          </Table>

          {data && <Pagination pagination={data.pagination} onPageChange={setPage} label="sites" />}
        </>
      )}

      {canCreate && (
        <CreateSiteDialog
          open={creating}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void mutate();
          }}
        />
      )}

      {canCreate && editing && (
        <SiteDialog
          site={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void mutate();
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────── edit ─────────────────────────────── */

/**
 * A site's own settings.
 *
 * A modal rather than a drawer: it is two fields. A drawer is a 28rem panel with
 * a scroll region and a pinned footer, and putting two fields in one leaves most
 * of a full-height column empty. When a site grows a crew list and an escalation
 * chain, the drawer is the right shape again - which is why `Drawer` is still
 * here and still documented.
 *
 * `company_id` is deliberately not here, matching `updateSiteSchema`: moving a
 * site between companies would silently re-tenant every assessment attached to
 * it, so it is a migration rather than an edit.
 *
 * Closing a site is not here either, but it is no longer missing: it is a row
 * action on the list. It stayed out of this dialog because it is not a setting -
 * it is an act with consequences that reach past the two fields above it, so it
 * wants a confirmation and a place to fail loudly, and a form with a Save button
 * gives it neither.
 *
 * The old objection to building it at all was that Postgres refuses to close a
 * site anyone still defaults to, so the control "would be a switch that fails
 * with a number in it" until this page could list those people. That was right
 * about the failure and wrong about needing the list:
 * `app.enforce_site_write_rules` raises with the count already in the message,
 * and `fromPostgrest` carries it through as a 400. See `toggleActive` above.
 */
function SiteDialog({
  site,
  onClose,
  onSaved,
}: {
  site: SiteRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const formId = React.useId();
  const toast = useToast();
  const [timezone, setTimezone] = React.useState(site.timezone);
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const patch: Record<string, unknown> = {};
  if (timezone.trim() !== site.timezone) patch.timezone = timezone.trim();

  const dirty = Object.keys(patch).length > 0;
  const requestClose = useDiscardGuard(dirty, saving, onClose);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await api(`/sites/${site.id}`, { method: "PATCH", body: patch });
      toast.success(`${site.name} saved`);
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save that location"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={() => void requestClose()}
      title={site.name}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => void requestClose()}>
            Cancel
          </Button>
          <Button form={formId} type="submit" size="sm" loading={saving} disabled={!dirty}>
            Save changes
          </Button>
        </>
      }
    >
      <Form id={formId} onSubmit={submit}>
        <div className="form-grid">
          {/* ── The name is set once, at creation ──────────────────────────
              It is on every appointment, visit and assessment already filed
              against this site, on exported reports a customer has kept, and in
              the scope switcher their people navigate by. Renaming makes all of
              those refer to something that no longer exists under that name.
              Same rule for a company, and the phone shows it the same way. */}
          <Field
            label="Location name"
            htmlFor="site-edit-name"
            hint="Set when the location was created and fixed afterwards."
          >
            <Input id="site-edit-name" value={site.name} disabled />
          </Field>

          <Field
            label="Timezone"
            htmlFor="site-edit-tz"
            required
            hint="Reports and digests bucket by this."
          >
            <TimezoneSelect id="site-edit-tz" value={timezone} onChange={setTimezone} />
          </Field>
        </div>

        <FormMessage>{error}</FormMessage>
      </Form>
    </Modal>
  );
}

/**
 * The new site lands in the company the console is pointed at.
 *
 * There used to be a company picker here for anybody unbounded, which is a
 * control that can only cause confusion now the list itself is scoped: create a
 * site in a company you are not looking at and it is filed correctly and
 * disappears. Switching company first is one click in the topbar and leaves the
 * page agreeing with itself.
 */
function CreateSiteDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const tenancy = useTenancy();
  const formId = React.useId();
  const toast = useToast();

  const [name, setName] = React.useState("");
  /**
   * Seeded from the company's existing sites, not from the browser.
   *
   * The calendar renders in the *site's* zone (decisions #25), so getting this
   * wrong moves every appointment on that site by half a day. The browser's zone
   * is a fact about where the administrator is standing, which for a dispatcher
   * in Hyderabad setting up a Texas yard is exactly the wrong answer - where the
   * company's other sites are at least a fact about the work. The phone made
   * this argument first and the console was still reading the browser.
   *
   * It falls back to the browser when there is nothing to inherit, and it is a
   * real, visible picker either way: a default nobody can see is one nobody can
   * correct.
   */
  const [timezone, setTimezone] = React.useState(
    () => tenancy.homeSite?.timezone ?? browserTimezone()
  );
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  // The zone as it was on first render. `browserTimezone` is a function - it is
  // passed to `useState` as a lazy initialiser above - so comparing against it
  // directly compares a string to a function and is always true.
  const seededTimezone = React.useRef(timezone);

  // The guard was on the dialog that *edits* a site and not on the one that
  // creates it, which is the wrong way round: an abandoned create loses a typed
  // name and a hand-picked zone, where an abandoned edit loses one of the two.
  const dirty = name.trim() !== "" || timezone !== seededTimezone.current;
  const requestClose = useDiscardGuard(dirty, submitting, onClose);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api("/sites", {
        method: "POST",
        body: {
          name,
          timezone,
          // Sent only when it is ours to choose. A company-bound caller's is
          // pinned server-side and whatever we sent would be discarded anyway.
          ...(tenancy.mustChooseCompany ? { company_id: tenancy.activeCompanyId } : {}),
        },
      });
      toast.success(`${name.trim()} created`, {
        description: "Invite people to it from the Team screen.",
      });
      setName("");
      onCreated();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not create that location"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => void requestClose()}
      title="Add a location"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => void requestClose()}>
            Cancel
          </Button>
          <Button form={formId} type="submit" size="sm" loading={submitting}>
            Create site
          </Button>
        </>
      }
    >
      <Form id={formId} onSubmit={submit}>
        <div className="form-grid">
          <Field
            label="Location name"
            htmlFor="site-name"
            required
            hint="A crew, depot, region or client location."
          >
            <Input
              id="site-name"
              autoFocus
              placeholder={PLACEHOLDER.siteName}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <Field label="Timezone" htmlFor="site-timezone" required hint="The location's own zone.">
            <TimezoneSelect id="site-timezone" value={timezone} onChange={setTimezone} />
          </Field>
        </div>

        <FormMessage>{error}</FormMessage>
      </Form>
    </Modal>
  );
}
